/**
 * Organization Page - Trade Loop v2.0
 *
 * Organization management page for Grow plan users
 * Allows organization admins to:
 * - View organization details
 * - Invite team members
 * - Manage existing members
 * - View and purchase additional seats
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { api } from "@/lib/api";
import { compressImage, isCompressibleImage } from "@/lib/imageUtils";
import { captureImageInto, isNativeCaptureAvailable } from "@/services/mediaCapture";
import { shareableUrl } from "@/utils/shareableUrl";
import AppLayout from "@/components/layout/AppLayout";
import { formatPhone } from "@/utils/formatPhone";
import { isNative } from "@/utils/platform";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ReferralDetailModal from "@/components/ReferralDetailModal";
import { ConfirmModal } from "@/components/ConfirmModal";
import CompactReferralCard from "@/components/CompactReferralCard";
import ProfilePreviewModal from "@/components/ProfilePreviewModal";
import { usePublicProfile, toProfilePreviewPartner } from "@/api/extensions/usePublicProfile";
import { isPlanPurchasable } from "@/config/subscription-plan-limits";
import {
  Building2,
  Users,
  UserPlus,
  Mail,
  Trash2,
  Crown,
  Settings,
  Plus,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Clock,
  XCircle,
  Copy,
  CreditCard,
  TrendingUp,
  Link2,
  UserCheck,
  Pencil,
  Globe,
  Phone,
  MapPin,
  Save,
  X,
  MoreVertical,
  MessageSquare,
  Send,
  LogOut,
  Eye,
  UserMinus,
  Upload,
  Image,
} from "lucide-react";

interface OrganizationMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string;
  status: string;
  joinedAt: string;
}

interface OrganizationInvite {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  status: string;
  inviteCode: string;
  expiresAt: string;
  createdAt: string;
}

interface SeatInfo {
  total: number;
  included: number;
  additional: number;
  used: number;
  pending: number;
  available: number;
  canPurchaseMore: boolean;
  /**
   * Card 24: the server's answer to "may additional seats be bought at all right now?".
   * Distinct from `canPurchaseMore`, which only means "under the seat cap" — the two disable the
   * same button but say different things to the user. Optional, and only ever used to disable:
   * the local plan config is the primary check, this catches the two configs drifting apart.
   */
  seatPurchaseEnabled?: boolean;
  maxTotal: number;
  costPerAdditional: number;
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  ownerId: string;
  teamEnabled: boolean;
  maxTeamMembers: number;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationStats {
  referrals: {
    total: number;
    pending: number;
    accepted: number;
    thisMonth: number;
  };
  connections: {
    total: number;
    pending: number;
    thisMonth: number;
  };
  team: {
    activeMembers: number;
  };
}

export default function OrganizationPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invites, setInvites] = useState<OrganizationInvite[]>([]);
  const [seatInfo, setSeatInfo] = useState<SeatInfo | null>(null);

  // Card 24. Seats are billed as part of Grow, so while Grow is "Coming Soon" they cannot be
  // bought. The local plan config is the primary check (it fails closed with no network call);
  // the server's own answer only ever disables further, so the two configs drifting apart can
  // never re-enable a purchase the API would refuse with 403 anyway.
  const seatPurchaseEnabled = isPlanPurchasable('grow') && seatInfo?.seatPurchaseEnabled !== false;

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteRole, setInviteRole] = useState<"member">("member");
  const [isInviting, setIsInviting] = useState(false);

  // Organization creation state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Organization stats state
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Team member referrals state
  const [teamMemberReferrals, setTeamMemberReferrals] = useState<any[]>([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);

  // All organization referrals state (admin only - referrals TO the owner)
  const [allOrgReferrals, setAllOrgReferrals] = useState<any[]>([]);
  const [isLoadingAllReferrals, setIsLoadingAllReferrals] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>('all');
  const [memberReferralsStatusFilter, setMemberReferralsStatusFilter] = useState<string>('all');
  const [myReferralsStatusFilter, setMyReferralsStatusFilter] = useState<string>('all');

  // Referral detail modal state
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<any | null>(null);

  // Ref for scrolling to invite form
  const inviteFormRef = useRef<HTMLDivElement>(null);

  // Ref for scrolling to edit form
  const editFormRef = useRef<HTMLDivElement>(null);

  // Contact details modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);

  // Profile Preview modal
  const [showProfilePreview, setShowProfilePreview] = useState(false);
  const [selectedProfileMember, setSelectedProfileMember] = useState<any | null>(null);
  // Fetch the team member's full public profile (same data the /network page
  // sees) so the preview modal renders bio, trades, market types, service
  // areas — not just the trimmed fields the org-members endpoint returns.
  const { data: memberProfileData } = usePublicProfile(
    selectedProfileMember?.id ?? null,
    showProfilePreview
  );

  // Leave organization confirmation modal state
  const [showLeaveConfirmModal, setShowLeaveConfirmModal] = useState(false);

  // Remove member confirmation modal state
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; name: string } | null>(null);

  // Organization edit state
  const [showEditForm, setShowEditForm] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    description: "",
    website: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "US",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Fetch organization data
  const fetchOrganizationData = async () => {
    try {
      setIsLoading(true);
      setIsLoadingStats(true);
      setIsLoadingReferrals(true);
      setIsLoadingAllReferrals(true);

      // ========================================================================
      // PERFORMANCE OPTIMIZATION: Single aggregated API call
      // Previously: ~2.6s (5 sequential API calls in waterfall)
      // Now: ~500-600ms (1 optimized backend call with parallel data fetching)
      // ========================================================================
      const response = await api.get("/api/workflows/organization/dashboard");
      console.log('[OrganizationPage] Dashboard API Response:', response);
      const dashboardData = response.data?.data || response.data;
      console.log('[OrganizationPage] Parsed dashboardData:', dashboardData);

      if (dashboardData?.organization) {
        console.log('[OrganizationPage] Organization found:', dashboardData.organization);

        // Set all data from single aggregated response
        setOrganization(dashboardData.organization);
        setMembers(dashboardData.members || []);

        // Admin-specific data (included in aggregated response)
        const isAdmin = dashboardData.role === 'admin' || dashboardData.isOwner;

        if (isAdmin) {
          setInvites(dashboardData.invites || []);
          setSeatInfo(dashboardData.seatInfo);
          setStats(dashboardData.stats);
          setAllOrgReferrals(dashboardData.referrals?.external || []);
          setTeamMemberReferrals(dashboardData.referrals?.teamMember || []);
        } else {
          // Team member view
          setInvites([]);
          setSeatInfo(null);
          setStats(null);
          setAllOrgReferrals([]);
          setTeamMemberReferrals(dashboardData.referrals?.teamMember || []);
        }
      } else {
        console.log('[OrganizationPage] No organization found in response');
        setOrganization(null);
        setMembers([]);
        setInvites([]);
        setSeatInfo(null);
        setStats(null);
        setAllOrgReferrals([]);
        setTeamMemberReferrals([]);
      }
    } catch (error: any) {
      console.error('[OrganizationPage] Error fetching organization dashboard:', error);
      console.error('[OrganizationPage] Error response:', error.response);
      if (error.response?.status !== 404) {
        console.error("Failed to fetch organization dashboard data:", error);
      }
      setOrganization(null);
      setMembers([]);
      setInvites([]);
      setSeatInfo(null);
      setStats(null);
      setAllOrgReferrals([]);
      setTeamMemberReferrals([]);
    } finally {
      setIsLoading(false);
      setIsLoadingStats(false);
      setIsLoadingReferrals(false);
      setIsLoadingAllReferrals(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchOrganizationData();
    }
  }, [authLoading]);

  // Create organization
  const handleCreateOrganization = async () => {
    if (!orgName.trim()) {
      showError("Name Required", "Please enter an organization name");
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.post("/api/workflows/organization/create", {
        name: orgName.trim(),
      });

      showSuccess("Organization Created", `${orgName} has been created successfully!`);
      setShowCreateForm(false);
      setOrgName("");
      fetchOrganizationData();
    } catch (error: any) {
      showError("Creation Failed", error.response?.data?.error || "Failed to create organization");
    } finally {
      setIsCreating(false);
    }
  };

  // Invite member via mailto (opens user's email client)
  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      showError("Email Required", "Please enter an email address");
      return;
    }

    if (!inviteFirstName.trim() || !inviteLastName.trim()) {
      showError("Name Required", "Please enter the member's first and last name");
      return;
    }

    if (!seatInfo || seatInfo.available <= 0) {
      showError("No Available Seats", "Please purchase additional seats to invite more members");
      return;
    }

    try {
      // Create invite via API (returns mailto link)
      const response = await api.post("/api/workflows/organization/invite-team-member", {
        email: inviteEmail.trim(),
        firstName: inviteFirstName.trim(),
        lastName: inviteLastName.trim(),
        role: inviteRole,
      });

      const mailtoLink = response.data?.mailtoLink;

      if (!mailtoLink) {
        showError("Invitation Failed", "Failed to generate email invitation");
        return;
      }

      // Open user's email client with pre-filled invitation
      window.location.href = mailtoLink;

      showSuccess("Email Draft Created", "Your email client should open with a pre-filled invitation");

      // Reset form
      setShowInviteForm(false);
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      setInviteRole("member");

      // Refresh to show new pending invite
      fetchOrganizationData();
    } catch (error: any) {
      console.error('Invite creation error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      showError("Invitation Failed", error.response?.data?.error || "Failed to create invitation");
    }
  };

  // Resend invite
  const handleResendInvite = async (inviteId: string) => {
    try {
      console.log('[Resend] Calling API with inviteId:', inviteId);
      const response = await api.post(`/api/workflows/organization/invite/${inviteId}/resend`);
      console.log('[Resend] Full response object:', response);
      console.log('[Resend] response.data:', response.data);
      console.log('[Resend] response.data.data:', response.data?.data);

      // Check if data is nested or direct
      const inviteData = response.data.data || response.data;
      console.log('[Resend] Using inviteData:', inviteData);

      const { email, firstName, lastName, organizationName, role, inviteLink, expiresAt } = inviteData;

      // Generate email body
      const subject = `Reminder: Invitation to join ${organizationName} on Trade Loop`;
      const body = `Hi ${firstName} ${lastName},

This is a reminder that you've been invited to join ${organizationName} on Trade Loop as a${role === 'admin' ? 'n admin' : ' member'}.

Click the link below to accept your invitation:
${inviteLink}

This invitation will expire on ${new Date(expiresAt).toLocaleDateString()}.

We look forward to having you on the team!

Best regards,
Trade Loop Team`;

      // Open mailto link
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;

      showSuccess("Opening Email Client", "Your email client will open with the invitation email");
    } catch (error: any) {
      console.error('[Resend] Error:', error);
      console.error('[Resend] Error response:', error.response);
      const errorMessage = error.response?.data?.error || error.message || "Failed to resend invitation";
      showError("Resend Failed", errorMessage);
    }
  };

  // Revoke invite
  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await api.delete(`/api/workflows/organization/invite/${inviteId}`);
      showSuccess("Invitation Revoked", "The invitation has been cancelled");
      fetchOrganizationData();
    } catch (error: any) {
      showError("Revocation Failed", error.response?.data?.error || "Failed to revoke invitation");
    }
  };

  // Remove member
  const handleRemoveMember = async (memberId: string, memberName: string) => {
    // Set the member to remove and show the modal
    setMemberToRemove({ id: memberId, name: memberName });
    setShowRemoveMemberModal(true);
  };

  const confirmRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      await api.delete(`/api/workflows/organization/members/${memberToRemove.id}`);
      showSuccess("Member Removed", `${memberToRemove.name} has been removed from the organization`);
      setShowRemoveMemberModal(false);
      setMemberToRemove(null);
      fetchOrganizationData();
    } catch (error: any) {
      showError("Removal Failed", error.response?.data?.error || "Failed to remove member");
      setShowRemoveMemberModal(false);
      setMemberToRemove(null);
    }
  };

  // Purchase seats
  const handlePurchaseSeats = async () => {
    // ⛔ Card 24 guard, at the handler and not only on the button's `disabled` prop. Additional
    // seats are a Grow purchase, and Grow is "Coming Soon" — so there is no checkout to start.
    // Removing a seat (handleCancelSeat) stays available: an org already paying for seats must
    // be able to stop.
    if (!seatPurchaseEnabled) {
      return;
    }

    // ⛔ Card 18 guard. Buying a seat is a purchase, and this path ends in a WebView navigation
    // to Stripe exactly like CheckoutButton did. It is NOT named in the card's task list — it
    // turned up in the audit — which is the point: any path reaching a Stripe URL has to be
    // guarded, and one missed call site is a store rejection.
    //
    // Seats are not routed through the plan hand-off token: that token is scoped to "start
    // checkout for plan X" and cannot express a seat quantity. Widening it to mean "any purchase"
    // would defeat the reason it is narrow. Org admins are a small population who are already
    // managing billing on the web, so this points them there rather than growing the credential.
    if (isNative) {
      showInfo(
        "Continue on the web",
        "Seats are purchased at gotradeloop.com/organization. Open it in your browser and sign in to add a seat."
      );
      return;
    }

    try {
      const response = await api.post("/api/workflows/organization/seats/purchase", {
        quantity: 1
      });

      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        showError("Purchase Failed", "No checkout URL returned from server");
      }
    } catch (error: any) {
      showError("Purchase Failed", error.response?.data?.error || "Failed to initiate seat purchase");
    }
  };

  // Cancel seats
  const handleCancelSeat = async () => {
    try {
      const response = await api.post("/api/workflows/organization/seats/cancel", {
        quantity: 1
      });

      showSuccess("Seat Removed", response.data?.message || "Seat removed. You will not be charged in your next billing cycle.");

      // Refresh organization data to show updated seat count
      await fetchOrganizationData();
    } catch (error: any) {
      showError("Cancellation Failed", error.response?.data?.error || "Failed to cancel seat");
    }
  };

  // Copy invite link
  const copyInviteLink = (inviteCode: string) => {
    // Public origin — an invite is opened by the recipient, not by this device.
    const link = shareableUrl(`/accept-invite/${inviteCode}`);
    navigator.clipboard.writeText(link);
    showSuccess("Link Copied", "Invite link copied to clipboard");
  };

  // Leave organization
  const handleLeaveOrganization = async () => {
    try {
      const response = await api.post('/api/workflows/organization/leave');
      showSuccess(
        'Left Organization',
        response.data?.message || 'You have successfully left the organization. Redirecting...'
      );
      setShowLeaveConfirmModal(false);

      // Wait a moment for user to see the message, then redirect to dashboard
      setTimeout(() => {
        // Reload to refresh user context
        window.location.href = '/dashboard';
      }, 2000);
    } catch (error: any) {
      showError(
        'Failed to Leave',
        error.response?.data?.error || 'An error occurred while leaving the organization'
      );
      setShowLeaveConfirmModal(false);
    }
  };

  // Handle logo file selection with auto-compression
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processLogoFile(file);
  };

  // Shared by the web file input and the native camera — both hand over a plain File.
  const processLogoFile = async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Invalid File', 'Please select an image file');
      return;
    }

    // Auto-compress if over 5MB
    let processedFile = file;
    if (file.size > 5 * 1024 * 1024) {
      if (isCompressibleImage(file)) {
        try {
          processedFile = await compressImage(file, 5, 0.85);
        } catch {
          showError('Compression Failed', 'Could not compress image. Please use a smaller file.');
          return;
        }
      } else {
        showError('File Too Large', 'Please select an image smaller than 5MB');
        return;
      }
    }

    setLogoFile(processedFile);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(processedFile);
  };

  // Open edit form with current data
  const openEditForm = () => {
    if (organization) {
      setEditData({
        name: organization.name || "",
        description: organization.description || "",
        website: organization.website || "",
        phone: organization.phone || "",
        addressLine1: organization.addressLine1 || "",
        addressLine2: organization.addressLine2 || "",
        city: organization.city || "",
        state: organization.state || "",
        postalCode: organization.postalCode || "",
        country: organization.country || "US",
      });
      setLogoFile(null);
      setLogoPreview(organization.logoUrl || null);
      setShowEditForm(true);
      // Scroll to edit form after a short delay to allow it to render
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  // Save organization info
  const handleSaveOrganization = async () => {
    if (!editData.name.trim()) {
      showError("Name Required", "Organization name cannot be empty");
      return;
    }

    setIsSaving(true);
    try {
      // First, upload logo if there's a new file
      let logoUrl = organization?.logoUrl;
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);

        const uploadResponse = await api.post('/api/files/organization-logo', formData);
        logoUrl = uploadResponse.data?.url || uploadResponse.data?.data?.url;
      }

      // Then update organization settings
      await api.put("/api/workflows/organization/settings", {
        name: editData.name.trim(),
        description: editData.description.trim() || null,
        website: editData.website.trim() || null,
        phone: editData.phone.trim() || null,
        addressLine1: editData.addressLine1.trim() || null,
        addressLine2: editData.addressLine2.trim() || null,
        city: editData.city.trim() || null,
        state: editData.state.trim() || null,
        postalCode: editData.postalCode.trim() || null,
        country: editData.country.trim() || "US",
        logoUrl: logoUrl || null,
      });

      showSuccess("Organization Updated", "Your organization information has been saved");
      setShowEditForm(false);
      setLogoFile(null);
      setLogoPreview(null);
      fetchOrganizationData();
    } catch (error: any) {
      showError("Update Failed", error.response?.data?.error || "Failed to update organization");
    } finally {
      setIsSaving(false);
    }
  };

  // Check for seat purchase callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const seatsResult = params.get("seats");

    if (seatsResult === "success") {
      showSuccess("Seats Purchased", "Additional seats have been added to your organization");
      // Clear the URL params
      window.history.replaceState({}, "", "/organization");
      fetchOrganizationData();
    } else if (seatsResult === "cancelled") {
      showError("Purchase Cancelled", "The seat purchase was cancelled");
      window.history.replaceState({}, "", "/organization");
    }
  }, []);

  // Check if user is an organization member (not admin/owner)
  // Remove back button for team members
  const isOrgMember = user?.organizationMembership?.role === 'member';

  if (authLoading || isLoading) {
    return (
      <AppLayout activeTab={isOrgMember ? "dashboard" : "profile"} title="Organization" backLink={isOrgMember ? undefined : "/settings"}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading organization...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activeTab={isOrgMember ? "dashboard" : "profile"} title="Organization" backLink={isOrgMember ? undefined : "/settings"}>
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pb-10 md:pb-0 space-y-6">
          {/* No Organization - Create One */}
          {!organization && !showCreateForm && (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-10 h-10 text-teal-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Create Your Organization</h2>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Set up your organization to invite team members and collaborate on referrals together.
                </p>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-8"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Organization
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Create Organization Form */}
          {!organization && showCreateForm && (
            <Card>
              <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-teal-600" />
                  </div>
                  <span>Create Organization</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Organization Name
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., ABC Plumbing Services"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleCreateOrganization}
                    disabled={isCreating || !orgName.trim()}
                    className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                  >
                    {isCreating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Organization"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateForm(false);
                      setOrgName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Organization Details */}
          {organization && (
            <>
              {/* Organization Header */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {organization.logoUrl ? (
                        <img
                          src={organization.logoUrl}
                          alt={`${organization.name} logo`}
                          className="w-10 h-10 rounded-xl object-cover border-2 border-teal-200"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-teal-600" />
                        </div>
                      )}
                      <div>
                        <span className="block">{organization.name}</span>
                        <span className="text-sm font-normal text-gray-500">
                          Created {new Date(organization.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user?.id === organization.ownerId && (
                        <Badge className="bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border-amber-200">
                          <Crown className="w-3 h-3 mr-1" />
                          Owner
                        </Badge>
                      )}
                      {user?.id === organization.ownerId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={openEditForm}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Organization Admin Contact Info - For Members Only */}
                  {isOrgMember && (() => {
                    // Find the organization admin/owner in members list
                    const admin = members.find(m => m.userId === organization.ownerId);

                    if (admin) {
                      return (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                            <Crown className="w-4 h-4 text-blue-600" />
                            Organization Administrator Contact
                          </h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <UserCheck className="w-4 h-4 text-blue-600" />
                              <span className="font-medium text-gray-900">
                                {admin.fullName}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-blue-600" />
                              <a
                                href={`mailto:${admin.email}`}
                                className="text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                {admin.email}
                              </a>
                            </div>
                            {admin.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-blue-600" />
                                <a
                                  href={`tel:${admin.phone}`}
                                  className="text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  {formatPhone(admin.phone)}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Organization Info */}
                  {(organization.description || organization.website || organization.phone) && (
                    <div className="mb-6 space-y-2 text-sm text-gray-600">
                      {organization.description && (
                        <p>{organization.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4">
                        {organization.website && (
                          <a
                            href={organization.website.startsWith("http") ? organization.website : `https://${organization.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-teal-600 hover:text-teal-700"
                          >
                            <Globe className="w-4 h-4" />
                            Website
                          </a>
                        )}
                        {organization.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {formatPhone(organization.phone)}
                          </span>
                        )}
                        {(organization.city || organization.state) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {[organization.city, organization.state].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Seat Usage */}
                  {seatInfo && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Team Seats</span>
                        <span className="text-sm text-gray-500">
                          {seatInfo.used} / {seatInfo.total} used
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-teal-500 to-emerald-500 h-3 rounded-full transition-all"
                          style={{ width: `${(seatInfo.used / seatInfo.total) * 100}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{seatInfo.included} included + {seatInfo.additional} additional</span>
                        <span>{seatInfo.available} available</span>
                      </div>
                      {!seatInfo.canPurchaseMore && seatInfo.total >= seatInfo.maxTotal && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-amber-800">
                              <strong>Maximum capacity reached.</strong> Your organization has reached the maximum of {seatInfo.maxTotal} total seats. To add more members, please remove existing members first.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons - Admin Only */}
                  {user?.id === organization.ownerId && (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        onClick={() => {
                          setShowInviteForm(true);
                          // Scroll to invite form after a short delay to allow it to render
                          setTimeout(() => {
                            inviteFormRef.current?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start'
                            });
                          }, 100);
                        }}
                        disabled={!seatInfo || seatInfo.available <= 0}
                        className="w-full sm:w-auto bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Invite Member
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handlePurchaseSeats}
                        disabled={!seatPurchaseEnabled || !seatInfo?.canPurchaseMore}
                        aria-disabled={!seatPurchaseEnabled || !seatInfo?.canPurchaseMore}
                        className="w-full sm:w-auto border-teal-300 hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {/* Card 24: "coming soon" and "you are at the cap" are different facts
                            and must not be reported as each other — telling a Grow admin they
                            have hit a limit they have not hit sends them to support. */}
                        {!seatPurchaseEnabled
                          ? 'Additional Seats Coming Soon'
                          : seatInfo?.canPurchaseMore
                            ? `Add Seats ($${seatInfo?.costPerAdditional || 5}/seat/mo)`
                            : 'Maximum Seats Reached'
                        }
                      </Button>
                      {seatInfo?.additional > 0 && (
                        <Button
                          variant="outline"
                          onClick={handleCancelSeat}
                          className="w-full sm:w-auto border-red-300 hover:bg-red-50 text-red-600"
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Remove 1 Seat
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Organization Edit Form */}
              {showEditForm && (
                <Card ref={editFormRef}>
                  <CardHeader className="bg-gradient-to-r from-gray-50 to-slate-50 border-b">
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                          <Settings className="h-5 w-5 text-gray-600" />
                        </div>
                        <span>Edit Organization</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowEditForm(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Organization Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          placeholder="e.g., ABC Plumbing Services"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Website
                        </label>
                        <Input
                          type="text"
                          value={editData.website}
                          onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                          placeholder="www.example.com"
                        />
                      </div>
                    </div>

                    {/* Logo Upload */}
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Organization Logo
                      </label>
                      <div className="flex flex-col sm:flex-row gap-4 items-start">
                        {/* Current/Preview Logo */}
                        <div className="flex-shrink-0">
                          {logoPreview ? (
                            <div className="relative group">
                              <img
                                src={logoPreview}
                                alt="Organization logo"
                                className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300"
                              />
                              {logoFile && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setLogoFile(null);
                                    setLogoPreview(organization?.logoUrl || null);
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove selected logo"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                              <Image className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Upload Button */}
                        <div className="flex-1">
                          <div className="flex flex-col gap-2">
                            {/* On native, intercept before the label activates its input
                                and open the app's own camera / photo-library sheet
                                instead of the WebView file picker. */}
                            <label
                              className="cursor-pointer"
                              onClick={(e) => {
                                if (!isNativeCaptureAvailable()) return;
                                e.preventDefault();
                                void captureImageInto(processLogoFile, showError);
                              }}
                            >
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoChange}
                                className="hidden"
                              />
                              <div className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                <Upload className="w-4 h-4 mr-2" />
                                {logoFile ? 'Change Logo' : 'Upload Logo'}
                              </div>
                            </label>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, or SVG. Max 5MB. Recommended: 400x400px
                            </p>
                            {logoFile && (
                              <p className="text-xs text-teal-600 flex items-center gap-1">
                                <Upload className="w-3 h-3" />
                                New logo selected: {logoFile.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Description
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                        rows={3}
                        value={editData.description}
                        onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                        placeholder="Brief description of your organization..."
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Phone Number
                      </label>
                      <PhoneInput
                        value={editData.phone}
                        onChange={(value) => setEditData({ ...editData, phone: value })}
                        placeholder="(555) 555-5555"
                      />
                    </div>

                    {/* Address */}
                    <div className="border-t pt-4">
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        Business Address
                      </label>
                      <div className="space-y-3">
                        <Input
                          type="text"
                          value={editData.addressLine1}
                          onChange={(e) => setEditData({ ...editData, addressLine1: e.target.value })}
                          placeholder="Street address"
                        />
                        <Input
                          type="text"
                          value={editData.addressLine2}
                          onChange={(e) => setEditData({ ...editData, addressLine2: e.target.value })}
                          placeholder="Suite, unit, building (optional)"
                        />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Input
                            type="text"
                            value={editData.city}
                            onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                            placeholder="City"
                            className="col-span-2 md:col-span-1"
                          />
                          <Input
                            type="text"
                            value={editData.state}
                            onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                            placeholder="State"
                          />
                          <Input
                            type="text"
                            value={editData.postalCode}
                            onChange={(e) => setEditData({ ...editData, postalCode: e.target.value })}
                            placeholder="ZIP"
                          />
                          <Input
                            type="text"
                            value={editData.country}
                            onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                            placeholder="Country"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={handleSaveOrganization}
                        disabled={isSaving || !editData.name.trim()}
                        className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                      >
                        {isSaving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowEditForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Organization Members Panel - Visible to all members and admin */}
              <Card>
                    <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <span>Team Members</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {members.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No team members found</p>
                      ) : (
                        <div className="space-y-3">
                          {members.map((member) => {
                            const isCurrentUser = member.userId === user?.id;
                            const isOrgOwner = member.userId === organization.ownerId;

                            return (
                              <div
                                key={member.id}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                              >
                                <div
                                  className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity flex-1"
                                  onClick={() => {
                                    setSelectedProfileMember({
                                      ...member,
                                      joinedAt: member.joinedAt,
                                      organizationId: organization.id
                                    });
                                    setShowProfilePreview(true);
                                  }}
                                >
                                  <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center">
                                    <UserCheck className="w-5 h-5 text-teal-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {member.fullName}
                                      {isCurrentUser && <span className="text-xs text-gray-500 ml-2">(You)</span>}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={
                                    member.role === 'admin'
                                      ? 'bg-amber-100 text-amber-700 border-amber-200 pointer-events-none'
                                      : 'bg-green-100 text-green-700 border-green-200 pointer-events-none'
                                  }>
                                    {member.role === 'admin' && <Crown className="w-3 h-3 mr-1" />}
                                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                  </Badge>

                                  {/* Dropdown Menu - Hidden for owner viewing their own row */}
                                  {!(isCurrentUser && user?.id === organization.ownerId) && (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                          <MoreVertical className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                      {isCurrentUser && user?.id !== organization.ownerId ? (
                                        // Current user (non-owner): Leave Organization
                                        <DropdownMenuItem
                                          className="text-red-600 focus:text-red-600"
                                          onClick={() => setShowLeaveConfirmModal(true)}
                                        >
                                          <LogOut className="w-4 h-4 mr-2" />
                                          Leave Organization
                                        </DropdownMenuItem>
                                      ) : user?.id === organization.ownerId && !isCurrentUser ? (
                                        // Logged-in user is org owner viewing other members: Send Message and Remove from Organization
                                        <>
                                          <DropdownMenuItem
                                            onClick={() => {
                                              const nameParts = (member.fullName || '').trim().split(' ');
                                              const firstName = nameParts[0] || '';
                                              const lastName = nameParts.slice(1).join(' ') || '';

                                              navigate('/messages', {
                                                state: {
                                                  autoSelectPartnerId: member.userId,
                                                  newConversationPartner: {
                                                    id: member.userId,
                                                    firstName,
                                                    lastName,
                                                    email: member.email
                                                  }
                                                }
                                              });
                                            }}
                                          >
                                            <MessageSquare className="w-4 h-4 mr-2" />
                                            Send Message
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            className="text-red-600 focus:text-red-600"
                                            onClick={() => handleRemoveMember(member.id, member.fullName)}
                                          >
                                            <UserMinus className="w-4 h-4 mr-2" />
                                            Remove from Organization
                                          </DropdownMenuItem>
                                        </>
                                      ) : isOrgOwner && !isCurrentUser ? (
                                        // Regular members viewing org owner: Send Message and Send Referral
                                        <>
                                          <DropdownMenuItem
                                            onClick={() => {
                                              const nameParts = (member.fullName || '').trim().split(' ');
                                              const firstName = nameParts[0] || '';
                                              const lastName = nameParts.slice(1).join(' ') || '';

                                              navigate('/messages', {
                                                state: {
                                                  autoSelectPartnerId: member.userId,
                                                  newConversationPartner: {
                                                    id: member.userId,
                                                    firstName,
                                                    lastName,
                                                    email: member.email
                                                  }
                                                }
                                              });
                                            }}
                                          >
                                            <MessageSquare className="w-4 h-4 mr-2" />
                                            Send Message
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => navigate(`/send-referral?partnerId=${member.userId}`)}
                                          >
                                            <Send className="w-4 h-4 mr-2" />
                                            Send Referral
                                          </DropdownMenuItem>
                                        </>
                                      ) : !isCurrentUser ? (
                                        // Regular members viewing other members: View Contact Details
                                        <DropdownMenuItem
                                          onClick={() => {
                                            setSelectedMember(member);
                                            setShowContactModal(true);
                                          }}
                                        >
                                          <Eye className="w-4 h-4 mr-2" />
                                          View Contact Details
                                        </DropdownMenuItem>
                                      ) : null}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>

              {/* My Referrals Panel - Team Members only */}
              {user?.organizationMembership && user.organizationMembership.role === 'member' && (
                  <Card>
                    <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                            <Link2 className="h-5 w-5 text-purple-600" />
                          </div>
                          <span>My Referrals</span>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                              <span className="text-xs">
                                {myReferralsStatusFilter === 'all' ? 'All Statuses' :
                                 myReferralsStatusFilter.charAt(0).toUpperCase() + myReferralsStatusFilter.slice(1)}
                              </span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setMyReferralsStatusFilter('all')}>
                              All Statuses
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMyReferralsStatusFilter('pending')}>
                              Pending
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMyReferralsStatusFilter('accepted')}>
                              Accepted
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMyReferralsStatusFilter('declined')}>
                              Declined
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMyReferralsStatusFilter('completed')}>
                              Completed
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setMyReferralsStatusFilter('cancelled')}>
                              Cancelled
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      {isLoadingReferrals ? (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                          <p className="text-gray-500 text-sm">Loading referrals...</p>
                        </div>
                      ) : teamMemberReferrals.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No referrals yet</p>
                      ) : (() => {
                          const filteredReferrals = teamMemberReferrals.filter((referral: any) =>
                            myReferralsStatusFilter === 'all' || referral.status === myReferralsStatusFilter
                          );

                          if (filteredReferrals.length === 0) {
                            return (
                              <p className="text-gray-500 text-center py-4">
                                No {myReferralsStatusFilter !== 'all' ? myReferralsStatusFilter : ''} referrals found
                              </p>
                            );
                          }

                          return (
                            <div className="space-y-3">
                              {filteredReferrals.map((referral: any) => (
                                <CompactReferralCard
                                  key={referral.id}
                                  referral={referral}
                                />
                              ))}
                            </div>
                          );
                        })()
                      }
                    </CardContent>
                  </Card>
              )}

              {/* Organization Stats Overview - Admin Only */}
              {user?.id === organization.ownerId && (
              <Card>
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b">
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-indigo-600" />
                    </div>
                    <span>Organization Overview</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {isLoadingStats ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
                    </div>
                  ) : stats ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Referrals Total */}
                      <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-700">Total Referrals</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-700">{stats.referrals.total}</p>
                        <p className="text-xs text-emerald-600 mt-1">
                          +{stats.referrals.thisMonth} this month
                        </p>
                      </div>

                      {/* Referrals Pending */}
                      <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-medium text-amber-700">Pending Referrals</span>
                        </div>
                        <p className="text-2xl font-bold text-amber-700">{stats.referrals.pending}</p>
                        <p className="text-xs text-amber-600 mt-1">
                          {stats.referrals.accepted} accepted
                        </p>
                      </div>

                      {/* Connections */}
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Link2 className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">Connections</span>
                        </div>
                        <p className="text-2xl font-bold text-blue-700">{stats.connections.total}</p>
                        <p className="text-xs text-blue-600 mt-1">
                          +{stats.connections.thisMonth} this month
                        </p>
                      </div>

                      {/* Team Members */}
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <UserCheck className="w-4 h-4 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">Team Members</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-700">{stats.team.activeMembers}</p>
                        <p className="text-xs text-purple-600 mt-1">
                          {stats.connections.pending} pending requests
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>No stats available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* External Referrals - Admin Only - Referrals from external partners */}
            {user?.id === organization.ownerId && (
              <Card>
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                        <Globe className="h-5 w-5 text-blue-600" />
                      </div>
                      <span>Recent Referrals (External)</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <span className="text-xs">
                            {selectedStatusFilter === 'all' ? 'All Statuses' :
                             selectedStatusFilter.charAt(0).toUpperCase() + selectedStatusFilter.slice(1)}
                          </span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedStatusFilter('all')}>
                          All Statuses
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatusFilter('pending')}>
                          Pending
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatusFilter('accepted')}>
                          Accepted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatusFilter('declined')}>
                          Declined
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatusFilter('completed')}>
                          Completed
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedStatusFilter('cancelled')}>
                          Cancelled
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {isLoadingAllReferrals ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-500 text-sm">Loading referrals...</p>
                    </div>
                  ) : allOrgReferrals.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No external referrals yet</p>
                  ) : (() => {
                      const filteredReferrals = allOrgReferrals.filter((referral: any) =>
                        selectedStatusFilter === 'all' || referral.status === selectedStatusFilter
                      );

                      if (filteredReferrals.length === 0) {
                        return (
                          <p className="text-gray-500 text-center py-4">
                            No {selectedStatusFilter !== 'all' ? selectedStatusFilter : ''} referrals found
                          </p>
                        );
                      }

                      return (
                        <div className="overflow-y-auto max-h-[520px] space-y-3 pr-2">
                          {filteredReferrals.map((referral: any) => (
                            <CompactReferralCard
                              key={referral.id}
                              referral={referral}
                            />
                          ))}
                        </div>
                      );
                    })()}
                </CardContent>
              </Card>
            )}

            {/* Member Referrals - Admin Only - Referrals from team members */}
            {user?.id === organization.ownerId && (
              <Card>
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                      <span>Member Referrals (Internal)</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <span className="text-xs">
                            {memberReferralsStatusFilter === 'all' ? 'All Statuses' :
                             memberReferralsStatusFilter.charAt(0).toUpperCase() + memberReferralsStatusFilter.slice(1)}
                          </span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setMemberReferralsStatusFilter('all')}>
                          All Statuses
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMemberReferralsStatusFilter('pending')}>
                          Pending
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMemberReferralsStatusFilter('accepted')}>
                          Accepted
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMemberReferralsStatusFilter('declined')}>
                          Declined
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMemberReferralsStatusFilter('completed')}>
                          Completed
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setMemberReferralsStatusFilter('cancelled')}>
                          Cancelled
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {isLoadingReferrals ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <p className="text-gray-500 text-sm">Loading referrals...</p>
                    </div>
                  ) : teamMemberReferrals.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No referrals from team members yet</p>
                  ) : (() => {
                      const filteredReferrals = teamMemberReferrals.filter((referral: any) =>
                        memberReferralsStatusFilter === 'all' || referral.status === memberReferralsStatusFilter
                      );

                      if (filteredReferrals.length === 0) {
                        return (
                          <p className="text-gray-500 text-center py-4">
                            No {memberReferralsStatusFilter !== 'all' ? memberReferralsStatusFilter : ''} referrals found
                          </p>
                        );
                      }

                      return (
                        <div className="space-y-3">
                          {filteredReferrals.map((referral: any) => (
                            <CompactReferralCard
                              key={referral.id}
                              referral={referral}
                            />
                          ))}
                        </div>
                      );
                    })()
                  }
                </CardContent>
              </Card>
            )}

              {/* Invite Member Form */}
              {showInviteForm && (
                <Card ref={inviteFormRef}>
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                        <UserPlus className="h-5 w-5 text-blue-600" />
                      </div>
                      <span>Invite Team Member</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          placeholder="John"
                          value={inviteFirstName}
                          onChange={(e) => setInviteFirstName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          placeholder="Doe"
                          value={inviteLastName}
                          onChange={(e) => setInviteLastName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Role: Team Member
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Team members can create referrals for admin approval but cannot manage the organization.
                      </p>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={handleInviteMember}
                        disabled={isInviting || !inviteEmail.trim() || !inviteFirstName.trim() || !inviteLastName.trim()}
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                      >
                        {isInviting ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Invitation
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowInviteForm(false);
                          setInviteEmail("");
                          setInviteFirstName("");
                          setInviteLastName("");
                          setInviteRole("member");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      An account will be created for this user and login credentials will be sent via email.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Pending Invites */}
              {invites.length > 0 && (
                <Card>
                  <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-amber-200 rounded-xl flex items-center justify-center">
                        <Clock className="h-5 w-5 text-amber-600" />
                      </div>
                      <span>Pending Invitations ({invites.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-gray-100">
                      {invites.map((invite) => (
                        <div
                          key={invite.id}
                          className="p-4 flex items-center justify-between hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                              <Mail className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {invite.firstName} {invite.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{invite.email}</p>
                              <p className="text-xs text-gray-400">
                                Expires {new Date(invite.expiresAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendInvite(invite.id)}
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              title="Resend invitation email"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyInviteLink(invite.inviteCode)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Copy invitation link"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRevokeInvite(invite.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Cancel invitation"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* Contact Details Modal */}
        {showContactModal && selectedMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-semibold text-gray-900">Contact Details</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowContactModal(false);
                    setSelectedMember(null);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="p-6 space-y-6">
                {/* Member Avatar and Name */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-semibold text-teal-600">
                      {selectedMember.firstName?.[0]}{selectedMember.lastName?.[0]}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedMember.firstName} {selectedMember.lastName}
                    </h3>
                    <Badge className={
                      selectedMember.role === 'admin'
                        ? 'bg-amber-100 text-amber-700 border-amber-200 pointer-events-none'
                        : selectedMember.role === 'member'
                        ? 'bg-blue-100 text-blue-700 border-blue-200 pointer-events-none'
                        : 'bg-green-100 text-green-700 border-green-200 pointer-events-none'
                    }>
                      {selectedMember.role === 'admin' && <Crown className="w-3 h-3 mr-1" />}
                      {selectedMember.role.charAt(0).toUpperCase() + selectedMember.role.slice(1)}
                    </Badge>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-gray-900">{selectedMember.email}</p>
                    </div>
                  </div>

                  {selectedMember.phone && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-500">Phone</p>
                        <p className="text-gray-900">{formatPhone(selectedMember.phone)}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Member Since</p>
                      <p className="text-gray-900">
                        {new Date(selectedMember.joinedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <UserCheck className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-500">Status</p>
                      <Badge className={
                        selectedMember.status === 'active'
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                      }>
                        {selectedMember.status.charAt(0).toUpperCase() + selectedMember.status.slice(1)}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 p-6 border-t bg-gray-50">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowContactModal(false);
                    setSelectedMember(null);
                  }}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

      {/* Referral Detail Modal */}
      {selectedReferral && (
        <ReferralDetailModal
          isOpen={showReferralModal}
          onClose={() => {
            setShowReferralModal(false);
            setSelectedReferral(null);
          }}
          referral={selectedReferral}
        />
      )}

      {/* Remove Member Confirmation Modal */}
      <ConfirmModal
        isOpen={showRemoveMemberModal}
        onClose={() => {
          setShowRemoveMemberModal(false);
          setMemberToRemove(null);
        }}
        onConfirm={confirmRemoveMember}
        title="Remove Team Member"
        message={
          <div className="space-y-3">
            <p className="font-medium text-gray-900">
              Are you sure you want to remove {memberToRemove?.name} from the organization?
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• They will lose access to organization resources</p>
              <p>• All referrals from this user will still be available</p>
              <p>• This seat will become available for a new member</p>
              <p className="font-medium text-red-600">• This action cannot be undone</p>
            </div>
          </div>
        }
        variant="danger"
        isDangerous={true}
        confirmText="Remove Member"
        cancelText="Cancel"
        size="md"
      />

      {/* Leave Organization Confirmation Modal */}
      <ConfirmModal
        isOpen={showLeaveConfirmModal}
        onClose={() => setShowLeaveConfirmModal(false)}
        onConfirm={handleLeaveOrganization}
        title="Leave Organization"
        message={
          <div className="space-y-3">
            <p className="font-medium text-gray-900">
              Are you sure you want to leave {organization?.name || 'this organization'}?
            </p>
            <div className="space-y-2 text-sm text-gray-600">
              <p>• Your individual subscription will be restored (or you'll be on the Starter plan if your subscription expired)</p>
              <p>• All your data, connections, and messages will be preserved</p>
              <p className="font-medium text-red-600">• This action cannot be undone</p>
            </div>
          </div>
        }
        variant="warning"
        isDangerous={true}
        confirmText="Leave Organization"
        cancelText="Cancel"
        size="md"
      />

      {/* Profile Preview Modal */}
      <ProfilePreviewModal
        isOpen={showProfilePreview}
        onClose={() => {
          setShowProfilePreview(false);
          setSelectedProfileMember(null);
        }}
        partner={
          memberProfileData
            ? {
                ...selectedProfileMember,
                ...toProfilePreviewPartner(memberProfileData),
                // Preserve org-member context (organizationId stamped by the
                // click handler, joinedAt) over the public-profile payload.
                organizationId: selectedProfileMember?.organizationId ?? memberProfileData.organizationId,
                joinedAt: selectedProfileMember?.joinedAt,
              }
            : selectedProfileMember
        }
        currentUserOrganizationId={organization?.id}
      />
    </AppLayout>
  );
}
